import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import TuneIcon from '@mui/icons-material/Tune';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  ProductFeatureService,
  type InventoryProductFeatureDto
} from './services/productFeatureService';

interface Branch {
  id: string;
  name: string;
}

interface InventoryProductFeatureModalProps {
  open: boolean;
  onClose: () => void;
}

const InventoryProductFeatureModal: React.FC<InventoryProductFeatureModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('accounting');
  const { branches, userDetails } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [features, setFeatures] = useState<InventoryProductFeatureDto[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<InventoryProductFeatureDto | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [featureName, setFeatureName] = useState('');
  const [editName, setEditName] = useState('');

  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');

  const companyId = userDetails?.companyId || '';

  const filteredFeatures = useMemo(() => {
    if (!selectedBranch?.id) return features;
    return features.filter(f => !f.branchesId || f.branchesId === selectedBranch.id);
  }, [features, selectedBranch?.id]);

  const showToast = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadFeatures = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await ProductFeatureService.getAll();
      setFeatures(data);
    } catch (err: any) {
      showToast(err?.message || 'Ek özellikler yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setError('');
      setFeatureName('');
      setEditName('');
      setSelectedFeature(null);
      loadFeatures();
    }
  }, [open, companyId]);

  const resetForm = () => {
    setFeatureName('');
    setEditName('');
    setSelectedFeature(null);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddFeature = async () => {
    const name = featureName.trim();
    if (!name) {
      setError('Özellik adı zorunludur');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await ProductFeatureService.create({
        companyId,
        name,
        branchesId: selectedBranch?.id || null,
        orderIndex: 0,
        isActive: true,
      });

      setFeatureName('');
      await loadFeatures();
      showToast('Ek özellik eklendi');
    } catch (err: any) {
      setError(err?.message || 'Ek özellik eklenemedi');
      showToast(err?.message || 'Ek özellik eklenemedi', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFeature = async () => {
    if (!selectedFeature) return;
    const name = editName.trim();
    if (!name) {
      setError('Özellik adı zorunludur');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await ProductFeatureService.update({
        id: selectedFeature.id,
        companyId,
        name,
        branchesId: selectedFeature.branchesId || null,
        orderIndex: selectedFeature.orderIndex || 0,
        isActive: selectedFeature.isActive ?? true,
      });

      setSelectedFeature(null);
      setEditName('');
      await loadFeatures();
      showToast('Ek özellik güncellendi');
    } catch (err: any) {
      showToast(err?.message || 'Ek özellik güncellenemedi', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeature = async (featureId: string) => {
    setDeletingId(featureId);
    try {
      await ProductFeatureService.delete(featureId);
      if (selectedFeature?.id === featureId) {
        setSelectedFeature(null);
        setEditName('');
      }
      await loadFeatures();
      showToast('Ek özellik silindi');
    } catch (err: any) {
      showToast(err?.message || 'Ek özellik silinemedi', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { height: '80vh', maxHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('stock.modals.productFeature.title')}</Typography>
          <IconButton edge="end" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('stock.modals.productFeature.form.newFeature')}
                </Typography>

                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedBranch}
                  onChange={(_, value) => setSelectedBranch(value)}
                  options={branches}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.modals.productFeature.form.branchOptional')}
                      placeholder={t('stock.modals.productFeature.form.branchPlaceholder')}
                      sx={{ mb: 2 }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 1, fontSize: '0.95rem', color: 'primary.main' }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                />

                <TextField
                  fullWidth
                  size="small"
                  label={t('stock.modals.productFeature.form.featureName')}
                  placeholder={t('stock.modals.productFeature.form.featureNamePlaceholder')}
                  value={featureName}
                  onChange={(e) => {
                    setFeatureName(e.target.value);
                    setError('');
                  }}
                  error={!!error}
                  helperText={error}
                />

                <Button
                  fullWidth
                  sx={{ mt: 2 }}
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  disabled={saving || !featureName.trim()}
                  onClick={handleAddFeature}
                >
                  {saving ? t('stock.common.saving') : t('stock.modals.productFeature.actions.add')}
                </Button>

                {selectedFeature && (
                  <Box sx={{ mt: 3, p: 2, border: '1px dashed #bdbdbd', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {t('stock.modals.productFeature.form.renameTitle')}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('stock.modals.productFeature.form.newFeatureName')}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      helperText={t('stock.modals.productFeature.form.currentName', { name: selectedFeature.name })}
                    />
                    <Button
                      fullWidth
                      sx={{ mt: 1.5 }}
                      variant="outlined"
                      startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
                      disabled={saving || !editName.trim() || editName.trim() === selectedFeature.name}
                      onClick={handleUpdateFeature}
                    >
                      {t('stock.modals.productFeature.actions.rename')}
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', maxHeight: '67vh', overflow: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TuneIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('stock.modals.productFeature.list.title')}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : filteredFeatures.length === 0 ? (
                  <Alert severity="info">{t('stock.modals.productFeature.list.empty')}</Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('stock.modals.productFeature.table.featureName')}</TableCell>
                        <TableCell>{t('stock.modals.productFeature.table.branch')}</TableCell>
                        <TableCell>{t('stock.modals.productFeature.table.usageCount')}</TableCell>
                        <TableCell align="right">{t('stock.modals.productFeature.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFeatures.map((feature) => {
                        const branchName = feature.branchesId
                          ? (branches.find(b => b.id === feature.branchesId)?.name || feature.branchesId)
                          : t('stock.modals.productFeature.general');
                        return (
                          <TableRow
                            key={feature.id}
                            hover
                            selected={selectedFeature?.id === feature.id}
                            onClick={() => {
                              setSelectedFeature(feature);
                              setEditName(feature.name);
                              setError('');
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{feature.name}</TableCell>
                            <TableCell>{branchName}</TableCell>
                            <TableCell>{feature.valueCount || 0}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFeature(feature.id);
                                }}
                                disabled={deletingId === feature.id}
                              >
                                {deletingId === feature.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">{t('stock.common.close')}</Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default InventoryProductFeatureModal;
